// src/tareas/DashboardGlobal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  Chip,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  MenuItem,
} from "@mui/material";

// Icons
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";

const ESTADOS = [
  { id: "pendiente", titulo: "Pendiente" },
  { id: "en_curso", titulo: "En curso" },
  { id: "resuelta", titulo: "Resuelta" },
  { id: "rechazada", titulo: "Rechazada" },
  { id: "cancelada", titulo: "Cancelada" },
];

// =========================
// Helpers (fecha / avatar / prioridad)
// =========================
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const authHeadersJson = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const formatearFechaCompleta = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const getIniciales = (usuario) => {
  const nombre = (usuario?.nombre || "").trim();
  const apellidos = (usuario?.apellidos || "").trim();
  const n1 = nombre ? nombre.split(" ")[0]?.[0] || "" : "";
  const a1 = apellidos ? apellidos.split(" ")[0]?.[0] || "" : "";
  const combo = (n1 + a1).toUpperCase();
  if (combo) return combo;
  if (nombre) return nombre[0].toUpperCase();
  return "?";
};

const sortPrioridad = (p) => {
  const pr = String(p || "normal").toLowerCase();
  if (pr === "critica") return 1;
  if (pr === "alta") return 2;
  if (pr === "normal") return 3;
  if (pr === "baja") return 4;
  return 99;
};

const prioridadChip = (p) => {
  const pr = String(p || "normal").toLowerCase();
  if (pr === "critica") return { label: "Crítica", variant: "filled", color: "error" };
  if (pr === "alta") return { label: "Alta", variant: "filled", color: "error" };
  if (pr === "baja") return { label: "Baja", variant: "filled", color: "default" };
  return { label: "Normal", variant: "filled", color: "default" };
};

// =========================
// Small UI helper: card de petición
// =========================
const PeticionCard = ({ item, creador, responsableNombre, onGestionar }) => {
  const depto = item?.departamento_nombre || "Sin departamento";
  const pr = prioridadChip(item?.prioridad);

  return (
    <Paper
      sx={{
        p: 1.5,
        backgroundColor: "#ffffff",
      }}
    >
      <Stack spacing={1}>
        {/* Cabecera mini: creador */}
        <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
          <Avatar sx={{ width: 34, height: 34 }}>
            {creador ? getIniciales(creador) : "?"}
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, lineHeight: 1.15 }} noWrap>
              {creador
                ? `${creador.nombre || ""} ${creador.apellidos || ""}`.trim()
                : "Creador desconocido"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Creador
            </Typography>
          </Box>
        </Stack>

        {/* Título */}
        <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
          {item?.titulo || "—"}
        </Typography>

        {/* Chips */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip label={pr.label} color={pr.color} variant={pr.variant} />
          <Chip label={depto} variant="filled" />
          <Chip label={`Estado: ${item?.estado || "—"}`} variant="filled" />
        </Box>

        {/* Responsable */}
        <Typography variant="body2">
          Responsable: <b>{responsableNombre || "Sin asignar"}</b>
        </Typography>

        {/* Vencimiento */}
        {item?.fecha_vencimiento ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Vence: {formatearFechaCompleta(item.fecha_vencimiento)}
          </Typography>
        ) : null}

        {/* Acción */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button size="small" startIcon={<SettingsIcon />} onClick={() => onGestionar(item)}>
            Gestionar
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export const DashboardGlobal = () => {
  const toast = useToast();

  // =========================
  // State: data + loading
  // =========================
  const [cargando, setCargando] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [usuariosMapa, setUsuariosMapa] = useState({}); // { [id]: usuario }

  // =========================
  // State: filtros
  // =========================
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  // =========================
  // State: panel gestión (Dialog)
  // =========================
  const [panelAbierto, setPanelAbierto] = useState(false);
  const [itemPanel, setItemPanel] = useState(null);

  const [usuariosDepto, setUsuariosDepto] = useState([]);
  const [cargandoUsuariosDepto, setCargandoUsuariosDepto] = useState(false);

  const [selectedUsuarioId, setSelectedUsuarioId] = useState(""); // "" | id string
  const [nuevoEstado, setNuevoEstado] = useState("");
  const [comentarioAccion, setComentarioAccion] = useState("");

  const [guardandoPanel, setGuardandoPanel] = useState(false);

  // =========================
  // Helpers: endpoint + carga
  // =========================
  const construirEndpoint = () => {
    const qs = new URLSearchParams();
    if (desde) qs.set("desde", desde);
    if (hasta) qs.set("hasta", hasta);
    const q = qs.toString();
    return `${Global.url}tareas/dashboard-global${q ? `?${q}` : ""}`;
  };

  const cargarUsuarios = async () => {
    try {
      const res = await fetch(`${Global.url}usuarios/todos`, { headers: authHeaders() });
      const data = await res.json().catch(() => []);
      if (res.ok && Array.isArray(data)) {
        const mapa = {};
        data.forEach((u) => (mapa[u.id] = u));
        setUsuariosMapa(mapa);
      }
    } catch (e) {
      console.error("Error cargando usuarios:", e);
    }
  };

  const cargarDashboard = async () => {
    setCargando(true);
    try {
      const endpoint = construirEndpoint();
      const res = await fetch(endpoint, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error("Error dashboard:", data);
        toast.error?.(data?.message || "No se pudo cargar el dashboard.");
        setDashboard(null);
        return;
      }

      setDashboard(data || null);
    } catch (e) {
      console.error("Error cargando dashboard:", e);
      toast.error?.("Error de red al cargar el dashboard.");
      setDashboard(null);
    } finally {
      setCargando(false);
    }
  };

  // =========================
  // Carga inicial
  // =========================
  useEffect(() => {
    cargarUsuarios();
    cargarDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================
  // Derivados (dashboard)
  // =========================
  const kpis = dashboard?.kpis || {};
  const prioritarias = Array.isArray(dashboard?.prioritariasPendientes) ? dashboard.prioritariasPendientes : [];
  const vencidas = Array.isArray(dashboard?.vencidas) ? dashboard.vencidas : [];
  const sinResponsable = Array.isArray(dashboard?.sinResponsable) ? dashboard.sinResponsable : [];
  const byDepto = Array.isArray(dashboard?.byDepartamento) ? dashboard.byDepartamento : [];

  const topDeptos = useMemo(() => byDepto.slice(0, 12), [byDepto]);

  // =========================
  // Panel: cargar usuarios del depto
  // =========================
  const cargarUsuariosDelDepto = async (departamentoId) => {
    if (!departamentoId) {
      setUsuariosDepto([]);
      return;
    }

    setCargandoUsuariosDepto(true);
    try {
      const res = await fetch(`${Global.url}departamentos/${departamentoId}/usuarios`, {
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => []);
      if (res.ok && Array.isArray(data)) {
        const activos = data.filter((u) => u.miembro_activo !== false);
        setUsuariosDepto(activos);
      } else {
        setUsuariosDepto([]);
      }
    } catch (e) {
      console.error("Error cargando usuarios depto:", e);
      setUsuariosDepto([]);
    } finally {
      setCargandoUsuariosDepto(false);
    }
  };

  // =========================
  // Panel: abrir/cerrar/guardar
  // =========================
  const abrirPanel = async (item) => {
    setItemPanel(item);
    setPanelAbierto(true);

    setSelectedUsuarioId(item?.asignado_a_usuario_id ? String(item.asignado_a_usuario_id) : "");
    setNuevoEstado(item?.estado || "pendiente");
    setComentarioAccion("");

    await cargarUsuariosDelDepto(item?.departamento_id);
  };

  const cerrarPanel = () => {
    if (guardandoPanel) return;
    setPanelAbierto(false);
    setItemPanel(null);
    setUsuariosDepto([]);
    setSelectedUsuarioId("");
    setNuevoEstado("");
    setComentarioAccion("");
    setGuardandoPanel(false);
  };

  const guardarPanel = async () => {
    if (!itemPanel) return;
    if (guardandoPanel) return;

    const peticionId = itemPanel.peticion_id;

    const asignPrev = itemPanel.asignado_a_usuario_id || null;
    const asignNuevo = selectedUsuarioId ? Number(selectedUsuarioId) : null;

    const hayCambioAsignacion = asignNuevo !== asignPrev;
    const hayCambioEstado = nuevoEstado && nuevoEstado !== itemPanel.estado;

    const hayComentario = typeof comentarioAccion === "string" && comentarioAccion.trim().length > 0;
    const comentario = hayComentario ? comentarioAccion.trim() : undefined;

    if (!hayCambioAsignacion && !hayCambioEstado && !hayComentario) {
      toast.info?.("No hay cambios que aplicar.");
      return;
    }

    setGuardandoPanel(true);

    try {
      // 1) asignación / comentario
      if (hayCambioAsignacion || hayComentario) {
        const body = {
          ...(hayCambioAsignacion && { asignado_a_usuario_id: asignNuevo }),
          ...(hayComentario && { comentario }),
        };

        const res = await fetch(`${Global.url}peticiones/${peticionId}`, {
          method: "PATCH",
          headers: authHeadersJson(),
          body: JSON.stringify(body),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          console.error("Error asignación/comentario:", data);
          toast.error?.(data?.message || "No se pudo guardar la asignación/comentario.");
          setGuardandoPanel(false);
          return;
        }
      }

      // 2) cambio de estado
      if (hayCambioEstado) {
        const res = await fetch(`${Global.url}tareas/peticiones/${peticionId}/estado`, {
          method: "PATCH",
          headers: authHeadersJson(),
          body: JSON.stringify({
            estado: nuevoEstado,
            ...(hayComentario && { comentario }),
          }),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          console.error("Error estado:", data);
          toast.error?.(data?.message || "No se pudo cambiar el estado.");
          setGuardandoPanel(false);
          return;
        }
      }

      toast.success?.("Cambios aplicados.");
      cerrarPanel();
      await cargarDashboard();
    } catch (e) {
      console.error("Error guardando panel:", e);
      toast.error?.("Error de red al guardar los cambios.");
    } finally {
      setGuardandoPanel(false);
    }
  };

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        {/* Header */}
        <Box className="pageHeader">
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Dashboard global
          </Typography>
          <Typography className="pageHeader__subtitle">
            Resumen del estado de tareas y accesos rápidos de gestión.
          </Typography>
        </Box>

        {/* Filtros */}
        <Paper sx={{ p: 2 }}>
          <Box className="filtersGrid">
            <TextField
              label="Desde (opcional)"
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={cargando}
            />

            <TextField
              label="Hasta (opcional)"
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={cargando}
            />

            <Box sx={{ display: "flex", gap: 1, alignItems: "end", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
              <Button type="button" onClick={cargarDashboard} disabled={cargando} sx={{ height: 40, minWidth: 140 }}>
                {cargando ? "Cargando..." : "Aplicar"}
              </Button>

              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  setDesde("");
                  setHasta("");
                  setTimeout(cargarDashboard, 0);
                }}
                disabled={cargando}
                sx={{ height: 40, minWidth: 120 }}
              >
                Limpiar
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Contenido */}
        {cargando ? (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CircularProgress size={20} />
              <Typography sx={{ color: "text.secondary" }}>Cargando dashboard…</Typography>
            </Box>
          </Paper>
        ) : !dashboard ? (
          <Paper sx={{ p: 2 }}>
            <Typography sx={{ color: "text.secondary", fontStyle: "italic" }}>
              No hay datos para mostrar.
            </Typography>
          </Paper>
        ) : (
          <>
            {/* KPIs */}
            <Box
              sx={{
                display: "grid",
                gap: 12,
                gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
              }}
            >
              <Paper sx={{ p: 2, backgroundColor: "#ffffff" }}>
                <Typography sx={{ fontWeight: 700, color: "text.secondary" }}>Abiertas</Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 900 }}>{kpis.abiertas ?? 0}</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Pendiente + En curso
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, backgroundColor: "#ffffff" }}>
                <Typography sx={{ fontWeight: 700, color: "text.secondary" }}>Vencidas (abiertas)</Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 900 }}>{kpis.vencidas ?? 0}</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Con fecha vencida
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, backgroundColor: "#ffffff" }}>
                <Typography sx={{ fontWeight: 700, color: "text.secondary" }}>Sin responsable</Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 900 }}>{kpis.sin_responsable ?? 0}</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Abiertas y sin asignación
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, backgroundColor: "#ffffff" }}>
                <Typography sx={{ fontWeight: 700, color: "text.secondary" }}>Sin tocar 48h</Typography>
                <Typography sx={{ fontSize: 28, fontWeight: 900 }}>{kpis.sin_tocar_48h ?? 0}</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Abiertas sin actividad reciente
                </Typography>
              </Paper>
            </Box>

            {/* Secciones */}
            <Box
              sx={{
                display: "grid",
                gap: 16,
                gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                alignItems: "start",
              }}
            >
              {/* Prioritarias */}
              <Paper sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 800, mb: 1 }}>
                  Prioritarias pendientes (alta/crítica)
                </Typography>

                <Stack spacing={1.25}>
                  {prioritarias.length === 0 ? (
                    <Typography sx={{ color: "text.secondary", fontStyle: "italic" }}>
                      No hay tareas prioritarias abiertas.
                    </Typography>
                  ) : (
                    prioritarias
                      .slice()
                      .sort((a, b) => sortPrioridad(a.prioridad) - sortPrioridad(b.prioridad))
                      .map((item) => {
                        const creador = item?.creador_id ? usuariosMapa[item.creador_id] : null;
                        const resp =
                          item?.asignado_a_usuario_id && usuariosMapa[item.asignado_a_usuario_id]
                            ? `${usuariosMapa[item.asignado_a_usuario_id].nombre || ""} ${
                                usuariosMapa[item.asignado_a_usuario_id].apellidos || ""
                              }`.trim()
                            : null;

                        return (
                          <PeticionCard
                            key={item.peticion_id}
                            item={item}
                            creador={creador}
                            responsableNombre={resp}
                            onGestionar={abrirPanel}
                          />
                        );
                      })
                  )}
                </Stack>
              </Paper>

              {/* Vencidas */}
              <Paper sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 800, mb: 1 }}>Vencidas (abiertas)</Typography>

                <Stack spacing={1.25}>
                  {vencidas.length === 0 ? (
                    <Typography sx={{ color: "text.secondary", fontStyle: "italic" }}>
                      No hay tareas vencidas.
                    </Typography>
                  ) : (
                    vencidas.map((item) => {
                      const creador = item?.creador_id ? usuariosMapa[item.creador_id] : null;
                      const resp =
                        item?.asignado_a_usuario_id && usuariosMapa[item.asignado_a_usuario_id]
                          ? `${usuariosMapa[item.asignado_a_usuario_id].nombre || ""} ${
                              usuariosMapa[item.asignado_a_usuario_id].apellidos || ""
                            }`.trim()
                          : null;

                      return (
                        <PeticionCard
                          key={item.peticion_id}
                          item={item}
                          creador={creador}
                          responsableNombre={resp}
                          onGestionar={abrirPanel}
                        />
                      );
                    })
                  )}
                </Stack>
              </Paper>

              {/* Sin responsable */}
              <Paper sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 800, mb: 1 }}>Sin responsable (abiertas)</Typography>

                <Stack spacing={1.25}>
                  {sinResponsable.length === 0 ? (
                    <Typography sx={{ color: "text.secondary", fontStyle: "italic" }}>
                      No hay tareas abiertas sin responsable.
                    </Typography>
                  ) : (
                    sinResponsable.map((item) => {
                      const creador = item?.creador_id ? usuariosMapa[item.creador_id] : null;

                      return (
                        <PeticionCard
                          key={item.peticion_id}
                          item={item}
                          creador={creador}
                          responsableNombre={null}
                          onGestionar={abrirPanel}
                        />
                      );
                    })
                  )}
                </Stack>
              </Paper>

              {/* Por departamento */}
              <Paper sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 800, mb: 1 }}>Abiertas por departamento (top)</Typography>

                {topDeptos.length === 0 ? (
                  <Typography sx={{ color: "text.secondary", fontStyle: "italic" }}>
                    No hay datos por departamento.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {topDeptos.map((d) => (
                      <Box
                        key={`${d.departamento_id}-${d.departamento_nombre}`}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1.5,
                          py: 0.75,
                          borderBottom: "1px solid rgba(0,0,0,0.06)",
                          "&:last-child": { borderBottom: "none" },
                        }}
                      >
                        <Typography sx={{ fontWeight: 700 }}>
                          {d.departamento_nombre || "Sin departamento"}
                        </Typography>

                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          <b>{d.abiertas}</b> abiertas
                          {typeof d.vencidas === "number" && d.vencidas > 0 ? (
                            <span>
                              {" "}
                              · <b>{d.vencidas}</b> vencidas
                            </span>
                          ) : null}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Box>
          </>
        )}

        {/* =========================
            Dialog: Panel de gestión
           ========================= */}
        <Dialog open={panelAbierto && !!itemPanel} onClose={cerrarPanel} maxWidth="sm">
          <DialogTitle
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}
          >
            Gestionar petición #{itemPanel?.peticion_id}
            <IconButton aria-label="Cerrar" onClick={cerrarPanel} disabled={guardandoPanel}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ pt: 2 }}>
            {!itemPanel ? null : (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Tarea
                  </Typography>
                  <Typography sx={{ fontWeight: 800 }}>{itemPanel.titulo}</Typography>
                </Box>

                <Box sx={{ display: "grid", gap: 1 }}>
                  <Typography variant="body2">
                    <b>Departamento:</b> {itemPanel.departamento_nombre || "Sin departamento"}
                  </Typography>
                  <Typography variant="body2">
                    <b>Estado actual:</b> {itemPanel.estado}
                  </Typography>
                  <Typography variant="body2">
                    <b>Responsable actual:</b>{" "}
                    {itemPanel.asignado_a_usuario_id && usuariosMapa[itemPanel.asignado_a_usuario_id]
                      ? `${usuariosMapa[itemPanel.asignado_a_usuario_id].nombre || ""} ${
                          usuariosMapa[itemPanel.asignado_a_usuario_id].apellidos || ""
                        }`.trim()
                      : "Sin responsable"}
                  </Typography>
                </Box>

                <Divider />

                {/* Reasignar + Estado */}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "end" } }}>
                  <Box sx={{ flex: 1 }}>
                    {cargandoUsuariosDepto ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          Cargando usuarios del departamento…
                        </Typography>
                      </Box>
                    ) : usuariosDepto.length === 0 ? (
                      <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                        No hay miembros activos en este departamento (o la tarea no tiene depto).
                      </Typography>
                    ) : (
                      <TextField
                        select
                        label="Reasignar a"
                        value={selectedUsuarioId}
                        onChange={(e) => setSelectedUsuarioId(e.target.value)}
                        disabled={guardandoPanel}
                      >
                        <MenuItem value="">Sin responsable</MenuItem>
                        {usuariosDepto.map((u) => (
                          <MenuItem key={u.id} value={String(u.id)}>
                            {`${u.nombre || ""} ${u.apellidos || ""}`.trim()}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Box>

                  <TextField
                    select
                    label="Estado de la petición"
                    value={nuevoEstado}
                    onChange={(e) => setNuevoEstado(e.target.value)}
                    disabled={guardandoPanel}
                    sx={{ flex: 1 }}
                  >
                    {ESTADOS.map((e) => (
                      <MenuItem key={e.id} value={e.id}>
                        {e.titulo}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>

                <TextField
                  label="Comentario"
                  value={comentarioAccion}
                  onChange={(e) => setComentarioAccion(e.target.value)}
                  placeholder="Añade contexto (aunque no cambies asignación/estado)…"
                  multiline
                  minRows={3}
                  disabled={guardandoPanel}
                />
              </Stack>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={cerrarPanel} disabled={guardandoPanel}>
              Cancelar
            </Button>
            <Button onClick={guardarPanel} disabled={guardandoPanel}>
              {guardandoPanel ? "Guardando..." : "Guardar cambios"}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};

export default DashboardGlobal;
